/**
 * SB Product Collection Campaigns
 * Product collection with logo and headline, aligned with SbGroupKeywordCampaigns design
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import { FaRocket, FaCheckCircle, FaFileAlt, FaImage, FaTag, FaFont, FaBoxes, FaPlus, FaTimes } from 'react-icons/fa';
import Layout from './Layout';
import SbProductCollectionAPI from '../services/SbProductCollectionAPI';
import '../assets/css/SbGroupKeywordCampaigns.css';

const SbProductCollectionCampaigns = () => {
    // Files
    const [targetingFile, setTargetingFile] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [customImageFile, setCustomImageFile] = useState(null);
    const [customImagePreview, setCustomImagePreview] = useState(null);
    const [negativeFile, setNegativeFile] = useState(null);

    // Config
    const [productAsins, setProductAsins] = useState(['']);
    const [headline, setHeadline] = useState('');
    const [dailyBudget, setDailyBudget] = useState(25);
    const [defaultBid, setDefaultBid] = useState(0.80);
    const [campaignState, setCampaignState] = useState('enabled');
    const [brandEntityId, setBrandEntityId] = useState('');
    const [isManualBrandEntry, setIsManualBrandEntry] = useState(false);
    const [availableBrands, setAvailableBrands] = useState([]);
    const [campaignName, setCampaignName] = useState('');

    // Data
    const [targeting, setTargeting] = useState([]);
    const [negatives, setNegatives] = useState([]);

    // UI State
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [step, setStep] = useState(1);

    const getProfileId = () => localStorage.getItem('selectedAccountValue') || null;

    useEffect(() => {
        fetchBrands();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchBrands = async () => {
        const profileId = getProfileId();
        if (profileId) {
            try {
                const response = await SbProductCollectionAPI.getBrands(profileId);
                if (response.success && response.brands) {
                    setAvailableBrands(response.brands);
                    if (response.brands.length > 0 && !brandEntityId) {
                        setBrandEntityId(response.brands[0].brandEntityId);
                    }
                }
            } catch (err) {
                console.error("Error fetching brands:", err);
            }
        }
    };

    // ASIN Handlers
    const addAsin = () => {
        if (productAsins.length < 3) setProductAsins([...productAsins, '']);
    };

    const removeAsin = (index) => {
        if (productAsins.length > 1) setProductAsins(productAsins.filter((_, i) => i !== index));
    };

    const updateAsin = (index, value) => {
        const updated = [...productAsins];
        updated[index] = value.toUpperCase();
        setProductAsins(updated);
    };

    // File Handlers
    const handleTargetingFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) setTargetingFile(file);
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file && (file.type.includes('image/png') || file.type.includes('image/jpeg'))) {
            setLogoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setLogoPreview(reader.result);
            reader.readAsDataURL(file);
        } else {
            setError('Logo must be PNG or JPG');
        }
    };

    const handleCustomImageChange = (e) => {
        const file = e.target.files[0];
        if (file && (file.type.includes('image/png') || file.type.includes('image/jpeg'))) {
            setCustomImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setCustomImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleNegativeFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) setNegativeFile(file);
    };

    // Generate Preview
    const handleGeneratePreview = async () => {
        if (!brandEntityId) {
            setError('Please select a Brand Entity ID');
            return;
        }
        if (!targetingFile) {
            setError('Please upload a target ASIN CSV');
            return;
        }
        if (!logoFile) {
            setError('Please upload the brand logo');
            return;
        }
        if (!customImageFile) {
            setError('Please upload a custom image (1200x628px). Amazon requires it since May 2024.');
            return;
        }
        if (!headline.trim()) {
            setError('Please enter a headline');
            return;
        }
        const validAsins = productAsins.filter(a => a.trim().length > 0);
        if (validAsins.length === 0) {
            setError('Enter at least one product ASIN to promote');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await SbProductCollectionAPI.uploadFiles(targetingFile, logoFile, customImageFile, negativeFile);

            if (response.success) {
                const data = response.data;
                setTargeting(data.products || []);
                if (data.negatives) setNegatives(data.negatives);

                const now = new Date();
                const dateStr = now.toISOString().slice(0, 10);
                const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
                setCampaignName(`SB_PC_${dateStr}_${timeStr}_${validAsins[0]}`);

                setStep(2);
                setResult({ success: true, message: `Preview generated. ${(data.products || []).length} target ASINs loaded.` });
            } else {
                setError(response.error || 'Error processing files');
            }
        } catch (err) {
            setError(err.message || 'Error generating preview');
        } finally {
            setLoading(false);
        }
    };

    // Create Campaign
    const handleCreateCampaign = async () => {
        const profileId = getProfileId();
        if (!profileId) {
            setError('Please select an account');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const startDateStr = tomorrow.toISOString().slice(0, 10);
            const endDate = new Date(tomorrow);
            endDate.setDate(endDate.getDate() + 30);
            const endDateStr = endDate.toISOString().slice(0, 10);

            const preparedTargeting = targeting.map(t => ({
                ...t,
                bid: t.bid || defaultBid
            }));

            const validAsins = productAsins.filter(a => a.trim().length > 0);

            const response = await SbProductCollectionAPI.createCampaign({
                profileId,
                campaignName,
                dailyBudget,
                startDate: startDateStr,
                endDate: endDateStr,
                defaultBid,
                state: campaignState,
                brandEntityId,
                headline,
                targeting: preparedTargeting,
                productAsins: validAsins,
                logo: logoFile,
                customImage: customImageFile,
                negatives: negatives.length > 0 ? negatives : null
            });

            if (response.success) {
                setResult({
                    success: true,
                    message: 'Campaign created successfully',
                    campaignId: response.data.campaign.campaignId
                });
                setStep(3);
            } else {
                setError(response.message || 'Error creating campaign');
            }
        } catch (err) {
            setError(err.message || 'Error creating campaign');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setTargetingFile(null);
        setLogoFile(null);
        setLogoPreview(null);
        setCustomImageFile(null);
        setCustomImagePreview(null);
        setNegativeFile(null);
        setTargeting([]);
        setNegatives([]);
        setProductAsins(['']);
        setHeadline('');
        setCampaignName('');
        setResult(null);
        setError(null);
        setStep(1);
    };

    const validAsinsCount = productAsins.filter(a => a.trim().length > 0).length;

    return (
        <Layout>
            <Container fluid className="sb-keyword-campaigns-page p-4">
                {/* Header */}
                <Row className="mb-4">
                    <Col>
                        <h2><FaBoxes className="me-2" />SB Product Collection Campaigns</h2>
                        <p className="text-muted">Product collection with logo and headline. Product-only targeting (ASINs).</p>
                    </Col>
                </Row>

                {/* Alerts */}
                {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
                {result && result.success && step < 3 && <Alert variant="success">{result.message}</Alert>}

                {/* Step 1: Setup */}
                {step === 1 && (
                    <>
                        {/* Config Section */}
                        <div className="config-section-card">
                            <Row className="g-3">
                                <Col md={4}>
                                    <label className="text-white mb-2 fw-bold">
                                        <FaTag className="config-label-icon" /> Brand Entity ID
                                    </label>
                                    <Form.Select
                                        className="custom-select" value={isManualBrandEntry ? 'MANUAL' : brandEntityId}
                                        onChange={e => {
                                            if (e.target.value === 'MANUAL') {
                                                setIsManualBrandEntry(true);
                                                setBrandEntityId('');
                                            } else {
                                                setIsManualBrandEntry(false);
                                                setBrandEntityId(e.target.value);
                                            }
                                        }}
                                    >
                                        <option value="">Select Brand...</option>
                                        {availableBrands.map((b, i) => (
                                            <option key={i} value={b.brandEntityId}>{b.brandEntityId} ({b.brandName || 'Unknown'})</option>
                                        ))}
                                        <option value="MANUAL">Enter manually...</option>
                                    </Form.Select>
                                    {isManualBrandEntry && (
                                        <Form.Control type="text" className="custom-input mt-2" placeholder="ENTITY..." value={brandEntityId} onChange={e => setBrandEntityId(e.target.value)} />
                                    )}
                                </Col>
                                <Col md={2}>
                                    <label className="text-white mb-2 fw-bold">Budget ($)</label>
                                    <Form.Control type="number" className="custom-input" value={dailyBudget} onChange={e => setDailyBudget(parseFloat(e.target.value))} min={1} />
                                </Col>
                                <Col md={2}>
                                    <label className="text-white mb-2 fw-bold">Bid ($)</label>
                                    <Form.Control type="number" className="custom-input" value={defaultBid} onChange={e => setDefaultBid(parseFloat(e.target.value))} min={0.02} step={0.01} />
                                </Col>
                                <Col md={2}>
                                    <label className="text-white mb-2 fw-bold">Status</label>
                                    <Form.Select className="custom-select" value={campaignState} onChange={e => setCampaignState(e.target.value)}>
                                        <option value="enabled">Enabled</option>
                                        <option value="paused">Paused</option>
                                    </Form.Select>
                                </Col>
                            </Row>
                            <Row className="g-3 mt-2">
                                <Col md={12}>
                                    <label className="text-white mb-2 fw-bold"><FaFont className="config-label-icon" /> Ad Headline *</label>
                                    <Form.Control type="text" className="custom-input" placeholder="Example: Discover our premium collection" value={headline} onChange={e => setHeadline(e.target.value)} maxLength={50} />
                                    <div className="input-helper-text">{headline.length}/50 characters</div>
                                </Col>
                            </Row>
                            <Row className="g-3 mt-3">
                                <Col md={12}>
                                    <label className="text-white mb-2 fw-bold">
                                        Products to Promote (1-3 ASINs) <Badge bg="info" className="ms-2">{validAsinsCount}/3</Badge>
                                    </label>
                                    {productAsins.map((asin, index) => (
                                        <div key={index} className="d-flex mb-2 gap-2">
                                            <Form.Control type="text" className="custom-input" placeholder={`ASIN ${index + 1}`} value={asin} onChange={e => updateAsin(index, e.target.value)} maxLength={10} style={{ maxWidth: '200px' }} />
                                            {productAsins.length > 1 && (
                                                <Button variant="outline-danger" size="sm" onClick={() => removeAsin(index)}><FaTimes /></Button>
                                            )}
                                        </div>
                                    ))}
                                    {productAsins.length < 3 && (
                                        <Button variant="outline-success" size="sm" onClick={addAsin}><FaPlus className="me-1" /> Add ASIN</Button>
                                    )}
                                </Col>
                            </Row>
                        </div>

                        {/* Upload Grid */}
                        <div className="upload-grid">
                            {/* Targeting CSV */}
                            <div className={`upload-card-new ${targetingFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Target ASINs (Competitors)</div>
                                <div className="upload-card-desc">CSV with ASINs where the ad will be shown</div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">{targetingFile ? 'Change File' : 'Select File'}</div>
                                    <input type="file" accept=".csv" onChange={handleTargetingFileChange} hidden />
                                </label>
                                {targetingFile && <small className="text-success mt-2">{targetingFile.name}</small>}
                            </div>

                            {/* Logo */}
                            <div className={`upload-card-new ${logoFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon">
                                    {logoPreview ? <img src={logoPreview} alt="Logo" style={{ maxHeight : '50px' }} /> : <FaImage />}
                                </div>
                                <div className="upload-card-title">Brand Logo</div>
                                <div className="upload-card-desc">PNG/JPG, recommended 400x400px</div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">{logoFile ? 'Change File' : 'Select File'}</div>
                                    <input type="file" accept="image/png,image/jpeg" onChange={handleLogoChange} hidden />
                                </label>
                                {logoFile && <small className="text-success mt-2">{logoFile.name}</small>}
                            </div>

                            {/* Custom Image */}
                            <div className={`upload-card-new ${customImageFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Required</div>
                                <div className="upload-icon">
                                    {customImagePreview ? <img src={customImagePreview} alt="Custom" style={{ maxHeight : '50px' }} /> : <FaImage />}
                                </div>
                                <div className="upload-card-title">Custom Image</div>
                                <div className="upload-card-desc">1200x628px required by Amazon</div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">{customImageFile ? 'Change File' : 'Select File'}</div>
                                    <input type="file" accept="image/png,image/jpeg" onChange={handleCustomImageChange} hidden />
                                </label>
                                {customImageFile && <small className="text-success mt-2">{customImageFile.name}</small>}
                            </div>

                            {/* Negative Products */}
                            <div className={`upload-card-new ${negativeFile ? 'has-file' : ''}`}>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Negative Products (Optional)</div>
                                <div className="upload-card-desc">ASINs to exclude from targeting</div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">{negativeFile ? 'Change File' : 'Select File'}</div>
                                    <input type="file" accept=".csv" onChange={handleNegativeFileChange} hidden />
                                </label>
                                {negativeFile && <small className="text-success mt-2">{negativeFile.name}</small>}
                            </div>
                        </div>

                        {/* Generate Button */}
                        <div className="generate-section">
                            <button className={`btn-generate-campaigns ${!loading ? 'ready' : ''}`} onClick={handleGeneratePreview} disabled={loading}>
                                {loading ? <><Spinner size="sm" animation="border" className="me-2" /> Processing...</> : <><FaRocket /> Generate Preview</>}
                            </button>
                        </div>

                        {/* Requirements */}
                        <div className="requirements-section">
                            <span className="requirements-title">Requirements - Product Collection</span>
                            <div className="req-item"><strong>Targeting:</strong> Products only (competitor ASINs).</div>
                            <div className="req-item"><strong>Products:</strong> 1-3 owned ASINs to promote.</div>
                            <div className="req-item"><strong>Logo:</strong> Required. PNG/JPG, 400x400px.</div>
                            <div className="req-item"><strong>Custom Image:</strong> Required. PNG/JPG, 1200x628px.</div>
                            <div className="req-item"><strong>Headline:</strong> Required. Maximum 50 characters.</div>
                            <div className="req-item"><strong>Suggested budget:</strong> $25/day, Bid: $0.80.</div>
                        </div>
                    </>
                )}

                {/* Step 2: Preview */}
                {step === 2 && (
                    <Card className="config-section-card">
                        <Card.Header className="bg-dark text-white">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5><FaRocket className="me-2" />Campaign Preview</h5>
                                <Button variant="outline-danger" size="sm" onClick={handleReset}>Reset</Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-4">
                                <Col md={4} className="text-center">
                                    {logoPreview && <img src={logoPreview} alt="Logo" style={{ maxHeight: '80px' }} className="mb-2" />}
                                    <p className="fw-bold">"{headline}"</p>
                                </Col>
                                <Col md={8}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="text-white">Campaign Name</Form.Label>
                                        <Form.Control type="text" className="custom-input" value={campaignName} onChange={e => setCampaignName(e.target.value)} />
                                    </Form.Group>
                                    <p><strong>Products:</strong> {productAsins.filter(a => a).join(', ')}</p>
                                    <p><strong>Target ASINs:</strong> {targeting.length} products</p>
                                    <p><strong>Budget:</strong> ${dailyBudget} | <strong>Bid:</strong> ${defaultBid}</p>
                                </Col>
                            </Row>
                            {targeting.length > 0 && (
                                <div style={{ maxHeight: '200px', overflowY: 'auto' }} className="mb-3">
                                    <Table hover variant="dark" size="sm">
                                        <thead><tr><th>Target ASIN</th><th>Bid</th></tr></thead>
                                        <tbody>
                                            {targeting.slice(0, 10).map((t, i) => (
                                                <tr key={i}>
                                                    <td>{t.asin || t.ASIN}</td>
                                                    <td>${t.bid || defaultBid}</td>
                                                </tr>
                                            ))}
                                            {targeting.length > 10 && <tr><td colSpan={2} className="text-center text-muted">... and {targeting.length - 10} more</td></tr>}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                            <div className="text-center">
                                <Button variant="success" size="lg" onClick={handleCreateCampaign} disabled={loading} className="fw-bold">
                                    {loading ? <><Spinner size="sm" animation="border" className="me-2" /> Creating...</> : <><FaCheckCircle className="me-2" /> Create in Amazon</>}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                )}

                {/* Step 3: Results */}
                {step === 3 && result && (
                    <Card className="bg-dark text-white border-0">
                        <Card.Header className="bg-success text-white">
                            <FaCheckCircle className="me-2" /> Campaign Created
                        </Card.Header>
                        <Card.Body className="text-center">
                            <h4 className="mb-4">{result.message}</h4>
                            {result.campaignId && <p>Campaign ID: <Badge bg="primary">{result.campaignId}</Badge></p>}
                            <Button variant="primary" onClick={handleReset}>Create Another Campaign</Button>
                        </Card.Body>
                    </Card>
                )}
            </Container>
        </Layout>
    );
};

export default SbProductCollectionCampaigns;

